import { useEffect } from 'react';

import { useAuthStore, usePlatformTOSStore } from '@/stores/store';

/**
 * Custom hook for platform Terms of Service management
 *
 * Checks TOS status on mount when user is authenticated.
 * Provides state for determining if TOS re-acceptance is required.
 *
 * @see PRP-4Q25-009 for TOS requirements
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { requiresReacceptance, tosContent, acceptPlatformTOS } = usePlatformTOS();
 *
 *   if (requiresReacceptance) {
 *     return <TOSReacceptancePage content={tosContent} onAccept={acceptPlatformTOS} />;
 *   }
 *
 *   return <MainApp />;
 * }
 * ```
 */
export function usePlatformTOS() {
  const { isAuthenticated } = useAuthStore();
  const {
    currentTOSVersion,
    acceptedTOSVersion,
    requiresReacceptance,
    tosCheckLoading,
    tosContent,
    changesSummary,
    effectiveDate,
    checkPlatformTOS,
    acceptPlatformTOS,
    loadAcceptedVersion,
    clearTOSState,
  } = usePlatformTOSStore();

  // Load accepted version and check TOS status on mount when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadAcceptedVersion();
      checkPlatformTOS();
    }
  }, [isAuthenticated, loadAcceptedVersion, checkPlatformTOS]);

  return {
    // State
    currentTOSVersion,
    acceptedTOSVersion,
    requiresReacceptance,
    tosCheckLoading,
    tosContent,
    changesSummary,
    effectiveDate,

    // Methods
    acceptPlatformTOS,
    clearTOSState,

    // Computed
    isLoading: tosCheckLoading,
    needsAcceptance: requiresReacceptance,
  };
}
